"use client";

import { animate, MotionConfig, motion, useReducedMotion } from "framer-motion";
import { type Ref, useEffect, useId, useRef, useState } from "react";
import { cn } from "../../../lib/utils";
import { uiLibrarySourceAttrs } from "../../../lib/ui-library/ui-library-source-attrs";

const THUMB_SPRING = {
  type: "spring",
  stiffness: 800,
  damping: 80,
  mass: 4,
} as const;

const SWITCH_SIZE = {
  sm: {
    root: "h-5 w-9 px-0.5",
    thumb: "size-4",
    stretch: "size-4",
    travel: 16,
  },
  md: {
    root: "h-7 w-12 px-1",
    thumb: "size-5",
    stretch: "size-5",
    travel: 20,
  },
} as const;

export interface SwitchProps {
  checked: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  interactive?: boolean;
  label?: string;
  ariaLabel?: string;
  size?: keyof typeof SWITCH_SIZE;
  className?: string;
}

export function Switch({
  checked,
  onCheckedChange,
  disabled,
  interactive = true,
  label,
  ariaLabel,
  size = "md",
  className,
}: SwitchProps) {
  const id = useId();
  const thumbRef = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const [isPressed, setIsPressed] = useState(false);
  const [isPointer, setIsPointer] = useState(false);
  const sizeClass = SWITCH_SIZE[size];

  useEffect(() => {
    if (!thumbRef.current || reduce) {
      return;
    }

    if (disabled && isPressed) {
      animate(
        thumbRef.current,
        { x: [0, -2, 2, -1, 0] },
        { delay: 0.2, duration: 0.6 },
      );
    }
  }, [disabled, isPressed, reduce]);

  const squish = !disabled && isPointer && isPressed && !reduce;

  return (
    <MotionConfig transition={reduce ? { duration: 0 } : THUMB_SPRING}>
      <span className={cn("inline-flex items-center gap-3", className)}>
        {interactive ? (
          <motion.button
            id={id}
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={ariaLabel ?? label}
            disabled={disabled}
            onClick={() => !disabled && onCheckedChange?.(!checked)}
            onPointerDown={(event) => {
              setIsPressed(true);
              setIsPointer(event.type.startsWith("pointer"));
            }}
            onPointerUp={() => setIsPressed(false)}
            onPointerLeave={() => setIsPressed(false)}
            initial={false}
            data-state={checked ? "checked" : "unchecked"}
            className={cn(
              "group peer inline-flex shrink-0 cursor-pointer items-center rounded-full outline-none transition-colors duration-200",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fintheon-accent)]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--fintheon-bg)]",
              "disabled:cursor-not-allowed disabled:opacity-60",
              checked
                ? "bg-[var(--fintheon-accent)]"
                : "bg-[var(--fintheon-text)]/15",
              sizeClass.root,
            )}
            {...uiLibrarySourceAttrs("beui-pro")}
          >
            <SwitchThumb
              thumbRef={thumbRef}
              checked={checked}
              squish={squish}
              travel={sizeClass.travel}
              thumbClassName={sizeClass.thumb}
              stretchClassName={sizeClass.stretch}
            />
          </motion.button>
        ) : (
          <motion.span
            aria-hidden="true"
            initial={false}
            data-state={checked ? "checked" : "unchecked"}
            className={cn(
              "group inline-flex shrink-0 items-center rounded-full transition-colors duration-200",
              checked
                ? "bg-[var(--fintheon-accent)]"
                : "bg-[var(--fintheon-text)]/15",
              sizeClass.root,
            )}
            {...uiLibrarySourceAttrs("beui-pro")}
          >
            <SwitchThumb
              thumbRef={thumbRef}
              checked={checked}
              squish={false}
              travel={sizeClass.travel}
              thumbClassName={sizeClass.thumb}
              stretchClassName={sizeClass.stretch}
            />
          </motion.span>
        )}
        {label ? (
          <label
            htmlFor={id}
            className="cursor-pointer text-sm text-[var(--fintheon-text)]"
          >
            {label}
          </label>
        ) : null}
      </span>
    </MotionConfig>
  );
}

function SwitchThumb({
  thumbRef,
  checked,
  squish,
  travel,
  thumbClassName,
  stretchClassName,
}: {
  thumbRef: Ref<HTMLDivElement>;
  checked: boolean;
  squish: boolean;
  travel: number;
  thumbClassName: string;
  stretchClassName: string;
}) {
  return (
    <motion.div
      ref={thumbRef}
      animate={{
        scale: squish ? 0.9 : 1,
        x: checked ? travel : 0,
      }}
      className={cn(
        "pointer-events-none block rounded-full bg-[var(--fintheon-bg)] shadow-sm",
        thumbClassName,
      )}
    >
      <div
        className={cn(stretchClassName, squish && (checked ? "ml-1" : "mr-1"))}
      />
    </motion.div>
  );
}
